import { Box, Button, TextField, TextFieldProps } from '@mui/material';
import {
  CalendarPickerView,
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import { parseISO } from 'date-fns';
import ja from 'date-fns/locale/ja';
import { forwardRef, ForwardedRef } from 'react';

import { dateConvertToFormat } from '@app/utils/format';

export declare type PickersActionBarAction =
  | 'clear'
  | 'cancel'
  | 'accept'
  | 'today';

const CustomActionBar = (props: PickersActionBarProps) => {
  const { onClear, actions } = props;
  const resolvedActions = Array.isArray(actions) ? actions : [];
  return (
    <Box textAlign="end" px={3} pb={1}>
      {resolvedActions?.includes('clear') && (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onClear}
        >
          クリア
        </Button>
      )}
    </Box>
  );
};

export interface DatePickerProps
  extends Omit<TextFieldProps, 'value' | 'onChange'> {
  actions?: PickersActionBarAction[];
  datePicker?: Omit<
    MuiDatePickerProps<Date | string, Date | string>,
    'renderInput' | 'onChange'
  >;
  disabled?: boolean;
  format?: string;
  inputFormat?: string;
  onChange?: (value?: string) => void;
  openTo?: CalendarPickerView;
  readOnly?: boolean;
  slotProps?: string;
  value?: string;
  views?: CalendarPickerView[];
}

export const DatePicker = forwardRef(function (
  {
    actions = [],
    disabled = false,
    format = 'yyyy/MM/dd',
    error = false,
    readOnly = false,
    onChange,
    placeholder = '2023/05/24',
    size = 'medium',
    sx,
    value,
    datePicker,
  }: DatePickerProps,
  ref: ForwardedRef<unknown>
) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <MuiDatePicker
        value={parseISO(value || '')}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            size={size}
            inputProps={{
              ...params.inputProps,
              placeholder,
              readOnly,
              value: dateConvertToFormat(value, datePicker?.inputFormat),
            }}
            sx={{ pr: 0, width: '160px', ...sx }}
          />
        )}
        componentsProps={{
          actionBar: {
            actions,
          },
        }}
        components={{
          ActionBar: CustomActionBar,
        }}
        onChange={(e) => {
          if (e !== 'Invalid Date') {
            onChange?.(dateConvertToFormat(e || '', format));
          }
        }}
        {...datePicker}
      />
    </LocalizationProvider>
  );
});
